import React from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { renderIf } from '@modules/utils';
import { hasPermission } from '@/constants/roles';
import { ICONS } from '@/constants/icons';
import { isSuperManager } from '@modules/logic/managerScope';
import { DRIVER_MODES, resolveDriverMode } from '@/hooks/useDriverMode';
import './sidebar.css';
import i18n from '@/i18n';
import LanguageSelector from '@/shared/ui/language_selector';
import { FN_HOME, FN_PREFIX, isFnCompany } from '@/constants/fnCompanies';

const SIDEBAR_ADMIN_ROLES = ['admin', 'super', 'administrative'];

const mapStateToProps = (state) => ({
  uRole: state.role,
  uUser: state.user,
  company: state.company,
  pendings: state.pendings || 0,
  language: state.language,
  currentManager: state.currentManager,
});

class Sidebar extends React.Component {
  constructor(props) {
    super(props);
    // Remember open/closed state across refreshes
    const saved = JSON.parse(localStorage.getItem('sidebar-open') || '{}');
    this.state = {
      open: {
        analytics: false,
        accounting: false,
        fleet: false,
        clients: false,
        planning: false,
        manage: false,
        ...saved,
      },
    };
  }

  toggle = (key) => {
    this.setState((prev) => {
      const next = { ...prev.open, [key]: !prev.open[key] };
      localStorage.setItem('sidebar-open', JSON.stringify(next));
      return { open: next };
    });
  };

  componentDidMount() {
    document.body.classList.remove('hold-transition');
    document.addEventListener('mousedown', this.handleOutsideClick);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
  }

  handleOutsideClick = (e) => {
    if (window.innerWidth >= 768) return;
    if (!document.body.classList.contains('sidebar-open')) return;
    if (e.target.closest('.main-sidebar')) return;
    document.body.classList.remove('sidebar-open');
  };

  handleSidebarClick = (e) => {
    if (window.innerWidth >= 768) return;
    if (e.target.closest('a')) {
      document.body.classList.remove('sidebar-open');
    }
  };

  render() {
    let logoUrl =
      'https://firebasestorage.googleapis.com/v0/b/mytransport-246817.appspot.com/o/frontends%2FMbility%2F60.png?alt=media&token=7375bf3a-0a5a-4614-94ee-2e5ba568cf50';

    if (window.location.host === 'gtp.mbility.app' || this.props.company === 'GTP') {
      logoUrl =
        'https://firebasestorage.googleapis.com/v0/b/mytransport-246817.appspot.com/o/frontends%2FGTP%2Flogo_gtp.png?alt=media&token=37da7904-9e63-4096-bd69-7c5c4c4520ad';
    } else if (window.location.host === 'easy.mbility.app' || this.props.company === 'EASY') {
      logoUrl =
        'https://firebasestorage.googleapis.com/v0/b/mytransport-246817.appspot.com/o/frontends%2FEasy%2Flogo_cooperativa_easy.png.webp?alt=media&token=14e8eb0e-7940-4fe9-9499-a2795874b969';
    }

    const { open } = this.state;
    const hasModule = (mod) =>
      SIDEBAR_ADMIN_ROLES.includes(this.props.uRole) ||
      this.props.currentManager?.modules?.[mod] === true;
    // Un super gestore accede all'anagrafica gestori (per vedere/gestire i propri
    // associati) anche senza il permesso VIEW_MANAGE degli admin MBILITY.
    const isSuper = isSuperManager(this.props.currentManager);
    // Modalità matricola: nessuna anagrafica autisti (vedi hooks/useDriverMode e
    // DriverModeGate in App.jsx, che protegge anche le rotte).
    const hasDriverRecords =
      SIDEBAR_ADMIN_ROLES.includes(this.props.uRole) ||
      resolveDriverMode(this.props.currentManager) !== DRIVER_MODES.MATRICOLA;
    // FN companies: the timeline replaces the dashboard, and the forked pages
    // live under `/fn` (see FnRedirectGate in App.jsx).
    const isFn = isFnCompany(this.props.uUser?.company);

    return (
      <aside
        id="sidebar"
        className="main-sidebar sidebar-dark-primary elevation-4 position-fixed"
        onClick={this.handleSidebarClick}
      >
        <a href="/" className="brand-link" aria-label="Home">
          <img
            src={logoUrl}
            alt={this.props.company || 'Logo'}
            className="brand-image img-circle elevation-3"
          />
        </a>

        <div className="sidebar">
          <nav className="mt-2">
            <ul
              className="nav nav-pills nav-sidebar flex-column"
              data-widget="treeview"
              role="menu"
              data-accordion="false"
            >
              {/* Dashboard (timeline for FN companies) */}
              {isFn ? (
                <li className="nav-item">
                  <NavLink to={FN_HOME} className="nav-link">
                    <i className={`nav-icon ${ICONS.timeline}`} aria-hidden="true"></i>
                    <p>{i18n.t('timeline')}</p>
                  </NavLink>
                </li>
              ) : (
                <li className="nav-item dashboard">
                  <NavLink to="/home" className="nav-link">
                    <i className="nav-icon fa-solid fa-gauge-high" aria-hidden="true"></i>
                    <p>{i18n.t('dashboard')}</p>
                    {this.props.pendings > 0 ? (
                      <span className="right badge badge-danger">{this.props.pendings}</span>
                    ) : null}
                  </NavLink>
                </li>
              )}

              {/* Map / History */}
              <li className="nav-item">
                <NavLink to="/map" className="nav-link">
                  <i className="nav-icon fa-solid fa-map" aria-hidden="true"></i>
                  <p>{i18n.t('mappa')}</p>
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink to={isFn ? `${FN_PREFIX}/history` : '/history'} className="nav-link">
                  <i className="nav-icon fa-solid fa-history" aria-hidden="true"></i>
                  <p>{i18n.t('storico')}</p>
                </NavLink>
              </li>

              {/* Prenotazioni (multimodale) */}
              {renderIf(
                hasModule('bookings'),
                <li className="nav-item">
                  <NavLink to="/home/bookings" className="nav-link">
                    <i className="nav-icon fa-solid fa-route" aria-hidden="true"></i>
                    <p>{i18n.t('prenotazioni', { defaultValue: 'Prenotazioni' })}</p>
                  </NavLink>
                </li>
              )}

              {/* Contabilità: fatture e tributi */}
              {renderIf(
                hasPermission(this.props.uUser?.role, 'VIEW_INVOICES'),
                <li className={`nav-item has-sub ${open.accounting ? 'menu-open' : ''}`}>
                  <button
                    type="button"
                    className="nav-link as-button"
                    onClick={() => this.toggle('accounting')}
                    aria-expanded={open.accounting}
                    aria-controls="submenu-accounting"
                  >
                    <i className="nav-icon fa-solid fa-calculator" aria-hidden="true"></i>
                    <p>{i18n.t('contabilita', { defaultValue: 'Contabilità' })}</p>
                    <i
                      className={`fa-solid fa-angle-right right chevron ${open.accounting ? 'rotated' : ''}`}
                      aria-hidden="true"
                    ></i>
                  </button>

                  <ul
                    id="submenu-accounting"
                    className="nav nav-treeview collapsible"
                    data-open={open.accounting}
                  >
                    <li className="nav-item">
                      <NavLink to="/invoices" className="nav-link">
                        <i className="nav-icon fa-solid fa-file-invoice" aria-hidden="true"></i>
                        <p>{i18n.t('fatture', { defaultValue: 'Fatture' })}</p>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/taxes" className="nav-link">
                        <i className="nav-icon fa-solid fa-landmark" aria-hidden="true"></i>
                        <p>{i18n.t('tributi', { defaultValue: 'Tributi' })}</p>
                      </NavLink>
                    </li>
                  </ul>
                </li>
              )}

              {/* Analytics */}
              {renderIf(
                hasPermission(this.props.uUser?.role, 'VIEW_ANALYTICS'),
                <li className={`nav-item has-sub ${open.analytics ? 'menu-open' : ''}`}>
                  <button
                    type="button"
                    className="nav-link as-button"
                    onClick={() => this.toggle('analytics')}
                    aria-expanded={open.analytics}
                    aria-controls="submenu-analytics"
                  >
                    <i className="nav-icon fa-solid fa-chart-bar" aria-hidden="true"></i>
                    <p>{i18n.t('analytics')}</p>
                    <i
                      className={`fa-solid fa-angle-right right chevron ${open.analytics ? 'rotated' : ''}`}
                      aria-hidden="true"
                    ></i>
                  </button>

                  <ul
                    id="submenu-analytics"
                    className="nav nav-treeview collapsible"
                    data-open={open.analytics}
                  >
                    <li className="nav-item">
                      <NavLink to="/analytics/home" className="nav-link">
                        <i className="nav-icon fa-solid fa-money-bill" aria-hidden="true"></i>
                        <p>{i18n.t('fatturato')}</p>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/analytics/b2c-market" className="nav-link">
                        <i className="nav-icon fa-solid fa-shopping-cart" aria-hidden="true"></i>
                        <p>Mercato B2C</p>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/analytics/forecast" className="nav-link">
                        <i className="nav-icon fa-solid fa-chart-column" aria-hidden="true"></i>
                        <p>Forecast</p>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/analytics/cash-forecast" className="nav-link">
                        <i className="nav-icon fa-solid fa-sack-dollar" aria-hidden="true"></i>
                        <p>Prev. cassa (Alpha)</p>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/analytics/kpi" className="nav-link">
                        <i className="nav-icon fa-solid fa-chart-line" aria-hidden="true"></i>
                        <p>{i18n.t('kpi')}</p>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/analytics/operators" className="nav-link">
                        <i className="nav-icon fa-solid fa-people-group" aria-hidden="true"></i>
                        <p>{i18n.t('operators')}</p>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/analytics/vehicles" className="nav-link">
                        <i className="nav-icon fa-solid fa-van-shuttle" aria-hidden="true"></i>
                        <p>{i18n.t('vehicles')}</p>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/analytics/drivers" className="nav-link">
                        <i className="nav-icon fa-solid fa-id-card" aria-hidden="true"></i>
                        <p>{i18n.t('drivers')}</p>
                      </NavLink>
                    </li>
                  </ul>
                </li>
              )}

              {/* Fleet */}
              {hasModule('fleet') && (
                <li className={`nav-item has-sub ${open.fleet ? 'menu-open' : ''}`}>
                  <button
                    type="button"
                    className="nav-link as-button"
                    onClick={() => this.toggle('fleet')}
                    aria-expanded={open.fleet}
                    aria-controls="submenu-fleet"
                  >
                    <i className="nav-icon fa-solid fa-truck-fast" aria-hidden="true"></i>
                    <p>{i18n.t('fleet')}</p>
                    <i
                      className={`fa-solid fa-angle-right right chevron ${open.fleet ? 'rotated' : ''}`}
                      aria-hidden="true"
                    ></i>
                  </button>

                  <ul
                    id="submenu-fleet"
                    className="nav nav-treeview collapsible"
                    data-open={open.fleet}
                  >
                    <li className="nav-item">
                      <NavLink to="/fleet/hubs" className="nav-link">
                        <i className="nav-icon fa-solid fa-bullseye" aria-hidden="true"></i>
                        <p>{i18n.t('hub')}</p>
                      </NavLink>
                    </li>
                    {hasDriverRecords && (
                      <li className="nav-item">
                        <NavLink to="/fleet/drivers" className="nav-link">
                          <i className="nav-icon fa-solid fa-id-card" aria-hidden="true"></i>
                          <p>{i18n.t('autisti')}</p>
                        </NavLink>
                      </li>
                    )}
                    <li className="nav-item">
                      <NavLink to="/fleet/vehicles" className="nav-link">
                        <i className="nav-icon fa-solid fa-van-shuttle" aria-hidden="true"></i>
                        <p>{i18n.t('mezzi')}</p>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/fleet/equipments" className="nav-link">
                        <i className="nav-icon fa-solid fa-toolbox" aria-hidden="true"></i>
                        <p>{i18n.t('Equipments')}</p>
                      </NavLink>
                    </li>
                    {/* Danni, rifornimenti e manutenzione riguardano solo i
                        mezzi: vivono come tab di /fleet/vehicles, non come voci
                        di menù. Qui restano le sezioni trasversali. */}
                    <li className="nav-item">
                      <NavLink to="/fleet/maintenance/scadenzario" className="nav-link">
                        <i className="nav-icon fa-solid fa-calendar-xmark" aria-hidden="true"></i>
                        <p>{i18n.t('scadenzario', { defaultValue: 'Scadenzario' })}</p>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/fleet/processes" className="nav-link">
                        <i className="nav-icon fa-solid fa-diagram-project" aria-hidden="true"></i>
                        <p>{i18n.t('pratiche', { defaultValue: 'Pratiche' })}</p>
                      </NavLink>
                    </li>
                  </ul>
                </li>
              )}

              {/* Planning */}
              {(hasModule('daily_trips') || hasModule('cdi') || hasModule('planner')) && (
                <li className={`nav-item has-sub ${open.planning ? 'menu-open' : ''}`}>
                  <button
                    type="button"
                    className="nav-link as-button"
                    onClick={() => this.toggle('planning')}
                    aria-expanded={open.planning}
                    aria-controls="submenu-planning"
                  >
                    <i className="nav-icon fa-solid fa-list-check" aria-hidden="true"></i>
                    <p>{i18n.t('pianificazione')}</p>
                    <i
                      className={`fa-solid fa-angle-right right chevron ${open.planning ? 'rotated' : ''}`}
                      aria-hidden="true"
                    ></i>
                  </button>

                  <ul
                    id="submenu-planning"
                    className="nav nav-treeview collapsible"
                    data-open={open.planning}
                  >
                    {hasModule('daily_trips') && (
                      <li className="nav-item">
                        <NavLink to="/planning/daily-trips" className="nav-link">
                          <i className="nav-icon fa-solid fa-calendar-day" aria-hidden="true"></i>
                          <p>{i18n.t('viaggi_singoli')}</p>
                        </NavLink>
                      </li>
                    )}
                    {hasModule('cdi') && (
                      <li className="nav-item">
                        <NavLink to="/planning/car-pooling" className="nav-link">
                          <i className="nav-icon fa-solid fa-car" aria-hidden="true"></i>
                          <p>{i18n.t('car_pooling', { defaultValue: 'Car Pooling' })}</p>
                        </NavLink>
                      </li>
                    )}
                    {hasModule('planner') && (
                      <li className="nav-item">
                        <NavLink to="/planning/planner" className="nav-link">
                          <i className="nav-icon fa-solid fa-calendar" aria-hidden="true"></i>
                          <p>{i18n.t('pianificatore')}</p>
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {/* Clients */}
              {(hasModule('users') ||
                hasModule('companies') ||
                isSuper ||
                hasPermission(this.props.uUser?.role, 'VIEW_MANAGE')) && (
                <li className={`nav-item has-sub ${open.clients ? 'menu-open' : ''}`}>
                  <button
                    type="button"
                    className="nav-link as-button"
                    onClick={() => this.toggle('clients')}
                    aria-expanded={open.clients}
                    aria-controls="submenu-clients"
                  >
                    <i className="nav-icon fa-solid fa-users" aria-hidden="true"></i>
                    <p>{i18n.t('clienti')}</p>
                    <i
                      className={`fa-solid fa-angle-right right chevron ${open.clients ? 'rotated' : ''}`}
                      aria-hidden="true"
                    ></i>
                  </button>

                  <ul
                    id="submenu-clients"
                    className="nav nav-treeview collapsible"
                    data-open={open.clients}
                  >
                    {hasModule('users') && (
                      <li className="nav-item">
                        <NavLink to="/users" className="nav-link">
                          <i className="nav-icon fa-solid fa-user" aria-hidden="true"></i>
                          <p>{i18n.t('utenti')}</p>
                        </NavLink>
                      </li>
                    )}
                    {hasModule('companies') && (
                      <li className="nav-item">
                        <NavLink to="/companies" className="nav-link">
                          <i className="nav-icon fa-solid fa-building" aria-hidden="true"></i>
                          <p>{i18n.t('companies')}</p>
                        </NavLink>
                      </li>
                    )}
                    {renderIf(
                      isSuper || hasPermission(this.props.uUser?.role, 'VIEW_MANAGE'),
                      <li className="nav-item">
                        <NavLink to="/gestori" className="nav-link">
                          <i className="nav-icon fa-solid fa-user-tie" aria-hidden="true"></i>
                          <p>{i18n.t('gestori')}</p>
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {/* B2B */}
              {renderIf(
                SIDEBAR_ADMIN_ROLES.includes(this.props.uRole) ||
                  hasPermission(this.props.uUser?.role, 'IMPERSONA'),
                <li className={`nav-item has-sub ${open.manage ? 'menu-open' : ''}`}>
                  <button
                    type="button"
                    className="nav-link as-button"
                    onClick={() => this.toggle('manage')}
                    aria-expanded={open.manage}
                    aria-controls="submenu-manage"
                  >
                    <i className="nav-icon fa-solid fa-book" aria-hidden="true"></i>
                    <p>{i18n.t('manage')}</p>
                    <i
                      className={`fa-solid fa-angle-right right chevron ${open.manage ? 'rotated' : ''}`}
                      aria-hidden="true"
                    ></i>
                  </button>

                  <ul
                    id="submenu-manage"
                    className="nav nav-treeview collapsible"
                    data-open={open.manage}
                  >
                    {renderIf(
                      SIDEBAR_ADMIN_ROLES.includes(this.props.uRole),
                      <li className="nav-item">
                        <NavLink to="/fleet/geofences" className="nav-link">
                          <i className="nav-icon fa-solid fa-draw-polygon" aria-hidden="true"></i>
                          <p>{i18n.t('geofences', { defaultValue: 'Geofence' })}</p>
                        </NavLink>
                      </li>
                    )}
                    {renderIf(
                      hasPermission(this.props.uUser?.role, 'IMPERSONA'),
                      <li className="nav-item">
                        <NavLink to="/users-role" className="nav-link">
                          <i className="nav-icon fa-solid fa-user-ninja" aria-hidden="true"></i>
                          <p>{i18n.t('impersona')}</p>
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}
            </ul>
          </nav>

          {/* Language Selector */}
          <div className="p-3">
            <LanguageSelector />
          </div>
        </div>
      </aside>
    );
  }
}

export default connect(mapStateToProps, null)(Sidebar);
